<template>
  <div>
    <v-container fluid>
      <v-row dense class="pb-1">
        <v-col cols="3">
          <v-autocomplete label="Process" dense outlined hide-details :items="processes" v-model="processId" item-text="name" item-value="id" @change="changeProcess()"/>
        </v-col>
        <v-col cols="3">
          <date-range :from.sync="from" :to.sync="to" v-model="dateDisplay" @change="refresh()" hide-details option="Last 365 days"/>
        </v-col>
        <v-col cols="3">
          <v-autocomplete label="Frequency" dense outlined hide-details v-model="frequency" @change="refresh()"
                    :items="['Daily','Weekly','Monthly','Quarterly','Yearly','Seasonal weekly','Seasonal monthly','Seasonal quarterly']"/>
        </v-col>
        <v-col class="text-right pr-2">
          <v-btn icon @click="print"><v-icon>mdi-printer</v-icon></v-btn>
          <v-tooltip bottom open-delay="1000" v-if="company.allowCSVExport">
            <template v-slot:activator="{on}">
              <v-btn icon @click="exportCSV" v-on="on"><v-icon>mdi-download</v-icon></v-btn>
            </template>
            Export as CSV file
          </v-tooltip>
        </v-col>
      </v-row>
      <v-row dense class="pb-0">
        <v-col class="text-center" cols="3">
          <v-btn-toggle rounded v-model="view" mandatory class="mr-4" @change="view===0?display=0: ''">
            <v-btn small>LAA</v-btn>
            <v-btn small>%</v-btn>
          </v-btn-toggle>
          <v-btn-toggle rounded v-model="display" mandatory class="mt-1" @change="display===1?buildGraph():''">
            <v-btn small :disabled="view===0"><v-icon small>mdi-format-list-bulleted</v-icon></v-btn>
            <v-btn small :disabled="view===0"><v-icon small>mdi-chart-line</v-icon></v-btn>
          </v-btn-toggle>
        </v-col>
        <v-col cols="3">
          <v-autocomplete label="Graph" dense outlined hide-details :disabled="view===0 || display===0" @change="buildGraph()"
                    :items="process.outputs" v-model="graph" item-text="spiritName" item-value="spiritId"/>
        </v-col>
      </v-row>
    </v-container>

    <v-card class="mx-4 pa-4 overflow-y" height="calc(100vh - 228px)">
      <div v-if="display===0" v-html="report()"/>
      <div v-else style='width:100%;text-align:center'>
         <div class="title">{{process.name}} - Process Performance</div>
         <div class="sub-title">{{from | formatDate}} to {{to | formatDate}}</div>
        <div ref="chart" style="height:400px"/>
      </div>
    </v-card>

  </div>
</template>

<script>
import fileSaver from 'file-saver';
import printHtml from '@/mixins/printHtml'
import googleCharts from '@/mixins/googleCharts'

export default {
  name: 'ReportsProcessPerformance',
  mixins: [printHtml,googleCharts],
  data() {
    return {
      from: null,
      to: null,
      dateDisplay: '',
      processes: [],
      processId: null,
      frequency: 'Daily',
      view: 0,
      display: 0,
      graph: '',
      processStatistics: [],
      processPerformance: [],
      chart: null,
      loading: true,
      options: {chartArea:{height:'70%',width:'85%'},
                fontSize:12,
                vAxis:{format: '#,###.0', title:"Percent of input",gridlines:{color:'#ddd'},minorGridlines:{count:0}},
                hAxis:{slantedText:true},
                series: {
                  0: {color:'#00f'},
                  1: {color:'#080',lineWidth:1},
                  2: {color:'#0f0',lineDashStyle:[5,5],lineWidth:1},
                  3: {color:'#0f0',lineDashStyle:[5,5],lineWidth:1},
                },
                legend:{ position: "none"}}
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    process() {
      return this.processes.find(p => p.id === this.processId) || {outputs:[]};
    },
  },
  async created() {
    this.$emit('name', 'Process performance')
    if (this.$route.query.from && this.$route.query.to) {
      this.from = this.$route.query.from;
      this.to = this.$route.query.to;
    }

    await this.$lazyListLoad(this.processes, 'processes2', 'id,name', 100, [{field:'active', comparator:'eq', value:'true'}]);
    if (this.$route.query.processId)
      this.processId = this.$route.query.processId;
    else
      this.processId = this.processes.length ? this.processes[0].id : null;
    this.changeProcess();
    this.loading = false;
  },
  methods: {
    async changeProcess() {
      const outputs = await this.processOutputs();
      this.graph = outputs.length ? outputs[0].spiritId : '';
      this.refresh();
    },
    async processOutputs() {
      if (!this.process.outputs) {
        const results = await this.$apiCall(`query ReportsProcessPerformance($id:ID!) {
                                               process(id:$id) {
                                                 outputs {spiritId,spiritName}
                                               }
                                             }`, {id:this.process.id});
        if (!results.error)
          this.process.outputs = results.data.process.outputs;
      }
      return this.process.outputs;
    },
    async refresh() {
      if (!this.processId)
        return;
      this.loading = true;
      const results = await this.$apiCall(`query ReportsProcessPerformance($id:ID!, $from:Date!,$to:Date!,$frequency:String!) {
                                                processStatistics(id:$id,from:$from,to:$to) {
                                                  spiritId,mean,stdDev
                                                }
                                                processPerformance(id:$id,from:$from,to:$to,frequency:$frequency) {
                                                  period,spirits{spiritId,LAA,percent,numDeviations}
                                                }
                                              }`,
                                          {id:this.processId,from:this.from,to:this.to,frequency:this.frequency});
      if (!results.error) {
        this.processStatistics = results.data.processStatistics;
        this.processPerformance = results.data.processPerformance;
        if (this.display === 1)
          this.buildGraph();
      }
      this.loading = false;
    },

    report() {
      if (this.loading || !this.process.outputs || !this.process.outputs.length)
        return '';
      const lines = [
                  `<style>
                    @page {size:portrait}
                    .reports-process-performance {font-family: Roboto, sans-serif; font-size:14px; width:100%;border-spacing:0}
                    .reports-process-performance th {height:36px;padding:0 16px}
                    .reports-process-performance td {height:36px;padding:0 16px}
                    .reports-process-performance .title {font-size:18px;text-transform:uppercase}
                    .reports-process-performance .sup-title {font-weight:normal;font-size:22px}
                    .reports-process-performance .sub-title {font-weight:normal;height:inherit;padding-bottom:20px}
                    .reports-process-performance .header th {border-bottom:1px solid #000;margin-bottom:20px}
                    .reports-process-performance .text-left {text-align:left}
                    .reports-process-performance .text-right {text-align:right}
                    .reports-process-performance .font-weight-bold {font-weight:bold}
                    .reports-process-performance .above-average {color:#090}
                    .reports-process-performance .below-average {color:#f00}
                  </style>
                  <table class="reports-process-performance">
                     <thead>
                        <tr>
                          <th colspan="6" class="sup-title">${this.company.name}</th>
                        </tr>
                       <tr>
                        <th colspan="6" class="title">${this.process.name} - Process Performance</th>
                      <tr>
                      <tr>
                         <th colspan="6" class="sub-title">${this.dateDisplay}</th>
                        </tr>
                      <tr class="header">
                          <th width="20%" class="text-left">Period</th>`
    ];
    const width = 80 / this.process.outputs.length;
    for (const spirit of this.process.outputs)
      lines.push(`<th width="${width}%" class="text-right">${spirit.spiritName}</th>`);
    lines.push(`</tr></thead><tbody>`);

    for (const line of this.processPerformance) {
      lines.push('<tr>');
      lines.push(`<td>${this.frequency === 'Daily' ? this.$formatDate(line.period) : line.period}</td>`);
      for (const output of this.process.outputs) {
        const spirit = line.spirits.find(s => s.spiritId === output.spiritId) ?? {numDeviations:0, LAA:0, percent:0};
        let color = '';
        if (spirit.numDeviations > 1)
          color = 'above-average';
        else if (spirit.numDeviations < -1)
          color = 'below-average';
        if (this.view === 0)
          lines.push(`<td class="text-right ${color}">${this.$number(spirit.LAA,3)} LAA</td>`);
        else
          lines.push(`<td class="text-right ${color}">${this.$number(spirit.percent,1) }%</td>`);
      }
      lines.push('</tr>');
    }

    lines.push(`</tbody>`);
    lines.push(`</table>`);
    return lines.join('');
    },

    async buildGraph() {
      await this.loadGoogleCharts();
      if (!this.chart && window.google) {
        window.google.charts.load('current', {'packages':['corechart']});
        this.chart = await new Promise(resolve => {
          window.google.charts.setOnLoadCallback(() => {
            if (this.$refs.chart == undefined)
              resolve(null);
            resolve(new window.google.visualization.LineChart(this.$refs.chart))
          });
        });
      }
      if (this.chart) {
        const dataLineChart = [["Period","Total",{role:"tooltip",type:"string"},"avg",{role:"tooltip",type:"string"},"up",{role:"tooltip",type:"string"},"down",{role:"tooltip",type:"string"}]];
        const statistics = this.processStatistics.find(s => s.spiritId === this.graph);
        if (!statistics)
          return;
        const standardColumns = [statistics.mean,`Average ${statistics.mean}%`,statistics.mean+statistics.stdDev,`Standard deviation ${statistics.stdDev}%`,statistics.mean-statistics.stdDev,`Standard deviation ${statistics.stdDev}%`];
        for (const line of this.processPerformance) {
          const value = line.spirits.find(s => s.spiritId === this.graph).percent;
          const period = this.frequency === 'Daily' ? this.$formatDate(line.period) : line.period;
          const row = [period,value,`${period}\n${value}%`];
          dataLineChart.push(row.concat(standardColumns));
        }
        const data = window.google.visualization.arrayToDataTable(dataLineChart);
        this.options.hAxis.showTextEvery = Math.max(Math.round(this.processPerformance.length / 10), 1);
        this.chart.draw(data, this.options);
      }

    },

    async print() {
      if (this.display === 0)
        this.printHtml('Process performance', this.report());
      else
        this.printHtml('Process performance', `<style>
                          @page {size:landscape}
                          body {font-family: Roboto, sans-serif; font-size:14px}
                          .title {font-weight:700;font-size:18px;text-align:center;width:100%}
                          .sub-title {text-align:center;margin-bottom:16px,width:100%}
                        </style>
                        <div class="title">${this.process.name} - Process Performance</div>
                        <div class="sub-title">${this.dateDisplay}</div>
                        <img id="graph"/>`,
                        {id:'graph', src:this.chart.getImageURI()});
    },

    exportCSV() {
      const lines = [ `"${this.company.name}"`,
                      `"${this.process.name} - Process Performance"`,
                      `"${this.dateDisplay}"`,
                    ];
      let line = `"Period"`;
      for (const spirit of this.process.outputs)
        line += `,"${spirit.spiritName}"`;
      lines.push(line);
      for (const l of this.processPerformance) {
        line = `"${l.period}"`;
        for (const output of this.process.outputs) {
          const spirit = l.spirits.find(s => s.spiritId === output.spiritId);
          line += this.view === 0 ? `,"${this.$number(spirit.LAA,3)} LAA"` : `,"${this.$number(spirit.percent,1)}%"`;
        }
        lines.push(line);
      }

      // Write CSV file
      const blob = new Blob([lines.join('\r\n')], {type: "text/plain;charset=utf-8"});
      fileSaver.saveAs(blob, 'Process performance.csv');
    }
  }
}
</script>